import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../../../Components/Form/FormField";
import TextareaForm from "../../../Components/Form/TextareaForm";
import Swal from "sweetalert2";
import RowLoader from "../../../Components/UI/RowLoader";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../../Components/UI/PageTitle";
import { useEffect, useState } from "react";
import {
  getPartnerNameAndImage,
  sendNewMessageFromAdminToPartner,
} from "../../../apis";
import FileInput from "../../../Components/Form/FileInput";
import Loader from "../../../Components/Loader";

const SendNewMessageToPartner = () => {
  const { partnerId } = useParams();
  const [image, setImage] = useState("");
  const [partnerData, setPartnerData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const initialValues = {
    message_title: "",
    message_details: "",
    message_image: "",
    confirm: false,
  };
  // Form Validation Schema
  const validationSchema = Yup.object().shape({
    message_title: Yup.string().required("Required"),
    message_details: Yup.string().required("Required"),
    confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const handleIntialValues = { ...initialValues };
    delete handleIntialValues.confirm;
    const handleValues = { ...values };
    delete handleValues.confirm;

    try {
      // Create FormData object
      const formData = new FormData();

      formData.append("message_title", values.message_title);
      formData.append("message_type", values.message_type);
      formData.append("message_details", values.message_details);

      if (values.message_image) {
        formData.append("message_image", values.message_image);
      }

      await sendNewMessageFromAdminToPartner(formData, partnerId);

      // Show success alert
      Swal.fire({
        title: "Success!",
        text: "Your message was sent successfully.",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        resetForm();
        setImage("");
        navigate("/admin-messages");
      });
    } catch (error) {
      console.error("Error submitting form:", error);

      // Show error alert
      Swal.fire({
        title: "Error!",
        text: error.response.data.message.join(" , "),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false); // Stop form submitting state
    }
  };

  const handleRemoveImage = (name, formField) => {
    setImage("");
    formField(name, "");
  };

  useEffect(() => {
    setIsLoading(true);
    getPartnerNameAndImage(partnerId)
      .then((response) => {
        setPartnerData(response.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [partnerId]);

  if (isLoading) return <Loader />;

  return (
    <>
      <PageTitle title="Send New Message" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="card !tw-rounded-xl">
            <div className="card-body tw-shadow-xl tw-rounded-xl">
              <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-2">
                  <img
                    src={partnerData?.userImage}
                    alt="User Avatar"
                    className="tw-w-full tw-rounded-full"
                  />
                </div>

                <h1 className="tw-text-xl !tw-m-0 tw-font-semibold !tw-text-gray-700">
                  {partnerData?.userName}
                </h1>
              </div>
              {partnerData.userActivationStatus ? (
                <>
                  <div className="row">
                    {/* Message Title */}
                    <FormField
                      required={false}
                      name="message_title"
                      label="Message Title"
                      placeholder="Message Title"
                      touched={touched}
                      errors={errors}
                      col_md="6"
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      value={values.message_title}
                    />
                    {/* Image */}
                    <FileInput
                      name="message_image"
                      label="Upload Message Image"
                      value={image}
                      onChange={setFieldValue}
                      onRemove={handleRemoveImage}
                      error={errors.message_image}
                      touched={touched.message_image}
                      setPhoto={setImage}
                      notRequired
                    />

                    {/* Message Details */}
                    <TextareaForm
                      required={false}
                      name="message_details"
                      placeholder="Message Details"
                      touched={touched.message_details}
                      errors={errors.message_details}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      label="Message Details"
                    />

                    {/* confirm */}
                    <div className="col-md-12 tw-flex tw-items-center">
                      <input
                        type="checkbox"
                        name="confirm"
                        id="confirm"
                        checked={values.confirm}
                        onChange={handleChange}
                        className="!tw-mr-2 tw-w-4 "
                      />
                      <label
                        htmlFor="confirm"
                        className="tw-text-sm tw-cursor-pointer !tw-m-0 !tw-mt-1"
                      >
                        I confirm that , I selected the correct partner to send
                        message{" "}
                      </label>
                    </div>
                    {errors.confirm && touched.confirm && (
                      <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                        {errors.confirm}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="tw-mt-4 tw-text-right">
                    <button
                      type="submit"
                      className="!tw-bg-primary btn tw-text-white !tw-w-[100px] hover:!tw-bg-primary/90 hover:tw-text-white"
                    >
                      {isSubmitting ? (
                        <RowLoader paddingStyle="tw-py-0" />
                      ) : (
                        "Send"
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <p className="tw-text-red-500 tw-text-center !tw-mt-6">
                  Cannot send message because the partner's account is not
                  active.
                </p>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SendNewMessageToPartner;
